import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, List } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Tabs from "@components/misc/Tabs";
import DestinationDetails from "./Components/DestinationDetails";
import { fetchIntegrationById, saveIntegrationAsync } from "@redux/slices/integrationSlice";
import { selectUserInfo } from "@redux/slices/authSlice";
import { parseError } from "../../utils/utils";
import tabsList from "@data/editor-tabs.json";

const AddIntegrationDestination = () => {

  const dispatch = useDispatch();
  const integrationDetails = useSelector((state) => state.integration.selectedIntegration);
  const status = useSelector((state) => state.integration.status);
  const error = useSelector((state) => state.integration.error);
  const userInfo = useSelector(selectUserInfo);

  const { id } = useParams();


  useEffect(() => {
    dispatch(fetchIntegrationById(id));
  }, [dispatch, id]);

  const navigate = useNavigate(); // Get the navigate function

  const handleGoToDashboard = () => {
    navigate(-1);
  };

  const onTabSelected = (pageName) => {
    // Use replace:true so tab switching doesn't pollute browser history
    navigate(`/integrations/${id}/${pageName.toLowerCase()}`, { replace: true });
  }


  const handleGoBack = () => {
    // router.back();
    navigate(`/integrations/${id}/source`);
  };

  const handleGotoInvocations = () => {
    navigate(`/integrations/${id}/invocations`);
  };

  const handleGoNext = () => {
    //router.push("/add-integration/source-details"); // Navigates to the /about page
    navigate(`/integrations/${id}/data-mapping`); // Navigate to the next page
  };

  const handleSave = async () => {
    // if (!integrationDetails || !integrationDetails.name || !integrationDetails.description) {
    //   toast.error("Please fill in both the Name and Description fields.");
    //   return;
    // }
    try {
      const resultAction = await dispatch(saveIntegrationAsync({ id, integrationDetails, status: 'Draft' }));
      if (saveIntegrationAsync.fulfilled.match(resultAction)) {
        toast.success('Integration saved successfully!');
      } else {
        toast.error(resultAction.payload?.message || 'Failed to save integration.');
      }
    } catch (err) {
      toast.error(parseError(err));
    } finally {
      // setIsLoading(false);
    }
  }

  return (
    <div className="mx-auto flex h-full min-h-0 max-w-full flex-col overflow-hidden bg-white">
      <section className="flex min-h-0 flex-1 flex-col">
        <div className="w-full border-b border-gray-100 bg-white px-4 sm:px-8 py-3">
          <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between">
            <div className="flex items-center gap-x-2 flex-shrink-0 justify-start min-w-[220px]">
              <button
                onClick={handleGoToDashboard}
                title="Back to listing"
                className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[100px]"
              >
                <ArrowLeft className="h-5 w-5" />
                Back
              </button>
              <button
                onClick={handleGotoInvocations}
                title="Invocations"
                className="flex items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-3 py-2 text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95"
              >
                <List className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 items-center justify-center">
              <Tabs tabsList={tabsList} selectedIndex={2} interactive={integrationDetails?.isNew === false} onTabSelected={onTabSelected} />
            </div>
            <div className="flex items-center justify-end gap-x-2 flex-shrink-0 min-w-[220px]">
              {/* Integrations shortcut */}
              <button
                onClick={() => navigate('/integrations/listing')}
                className="flex whitespace-nowrap items-center gap-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-5 py-2 text-xs font-bold text-[#1e3a8a] hover:bg-[#e0efff] hover:border-[#b8d9ff] transition-all shadow-sm active:scale-95"
              >
                Integrations
              </button>
              <button
                onClick={handleGoBack}
                className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-6 py-2 text-xs font-semibold text-[#1e3a8a] transition-all duration-200 hover:bg-[#e0efff] hover:border-[#b8d9ff] shadow-sm active:scale-95 min-w-[100px]"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-[#3b82f6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Prev
              </button>
              {userInfo && userInfo.role && userInfo.role.toLowerCase() !== "user" && (
                <button
                  onClick={handleSave}
                  className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-xs font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[100px]"
                >
                  Save
                </button>
              )}
              <button
                onClick={handleGoNext}
                className="flex whitespace-nowrap items-center justify-center gap-x-2 rounded-full bg-blue-600 px-6 py-2 text-xs font-semibold text-white transition-all duration-200 hover:bg-blue-700 shadow-lg hover:shadow-blue-200 active:scale-95 min-w-[100px]"
              >
                Next
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-12">
          <div className="mx-auto w-full max-w-[1400px]">
            <DestinationDetails details={integrationDetails} isLoading={status === 'loading'} error={error} userRole={userInfo?.role} />
          </div>
        </div>
      </section>
    </div>
  )
}

export default AddIntegrationDestination;